import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { determineGlobalFilterDefinitions } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineGlobalFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getGlobalFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedGlobalFilterValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { isFilterStoreValueEqual } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { HorizontalFilterBarItemProps } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { getTranslationsForGlobalFilterChip } from 'apps/sigma2-ui/src/filters-v2/lib/filter-utils';
import { SecondaryFilterChip } from 'apps/sigma2-ui/src/filters-v2/components/SecondaryFilterChip';
import { SecondaryFilterButton } from 'apps/sigma2-ui/src/filters-v2/components/SecondaryFilterButton';
import React from 'react';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { GlobalFiltersMenu } from 'apps/sigma2-ui/src/filters-v2/components/GlobalFiltersMenu';

// eslint-disable-next-line complexity
export const determineGlobalFilterBarItems = ({
  store,
  organizationId,
  databaseId,
  databaseRevisionId,
  setGlobalFilters,
  myObservation,
  stagingGlobalFilters,
  resetStagingGlobalFilters,
  setStagingGlobalFilter,
  setSelectedGlobalFilterKey,
  selectedGlobalFilterKey,
  disabled,
  language,
  scopeId,
  isEmbedMode,
}: {
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  setGlobalFilters: (params: {
    globalFilterSelections: SecondaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  myObservation: MyObservation | null;
  stagingGlobalFilters: SecondaryFilterSelection[];
  resetStagingGlobalFilters: () => void;
  setStagingGlobalFilter: (params: {
    secondaryFilterKey: string;
    value: FilterStoreValue;
  }) => void;
  selectedGlobalFilterKey: string | null;
  setSelectedGlobalFilterKey: (key: string | null) => void;
  disabled: boolean;
  language: Phrases;
  scopeId: string;
  isEmbedMode: boolean;
}): HorizontalFilterBarItemProps[] => {
  const globalFilterDefinitions = determineGlobalFilterDefinitions({
    filterSetDefinitionsWithValues:
      myObservation?.filterSetDefinitionsWithValues ?? [],
  });

  const allItems: HorizontalFilterBarItemProps[] = [];

  for (const filterDefinition of globalFilterDefinitions) {
    const currentValue = getSelectedGlobalFilterValue({
      filterKey: filterDefinition.columnHeaderValue,
      store,
      organizationId,
      databaseId,
      databaseRevisionId,
      scopeId,
    });

    if (currentValue === null) {
      continue;
    }

    const defaultValue: string[] = [];

    if (
      defaultValue === null ||
      isFilterStoreValueEqual(currentValue, defaultValue)
    ) {
      continue;
    }

    const fullKey = getGlobalFilterFullKey({
      filterKey: filterDefinition.columnHeaderValue,
    });

    allItems.push({
      key: fullKey,
      content: ({ menuOpened, openMenu }) => {
        const translations = getTranslationsForGlobalFilterChip({
          filterKey: filterDefinition.columnHeaderValue,
          store,
          databaseRevisionId,
          databaseId,
          organizationId,
          myObservation,
          language,
          scopeId,
        });

        if (!translations) {
          return null;
        }

        return (
          <SecondaryFilterChip
            disabled={disabled}
            active={menuOpened}
            hasSelection={true}
            icon={null}
            canDismiss={!isEmbedMode}
            onDismiss={() => {
              const current = determineGlobalFilterSelections({
                myObservation,
                store,
                organizationId,
                databaseId,
                databaseRevisionId,
                scopeId,
              });

              const newSelections: SecondaryFilterSelection[] = current.map(
                (item) =>
                  item.secondaryFilterKey === filterDefinition.columnHeaderValue
                    ? {
                        ...item,
                        value: defaultValue,
                      }
                    : item,
              );

              setGlobalFilters({
                globalFilterSelections: newSelections,
              });
            }}
            onClick={() => {
              resetStagingGlobalFilters();
              setSelectedGlobalFilterKey(filterDefinition.columnHeaderValue);
              openMenu();
            }}
            leftTranslatedContent={translations.leftTranslatedContent}
            rightTranslatedContent={translations.rightTranslatedContent}
          />
        );
      },
      menu: ({ closeMenu }) => {
        return (
          <GlobalFiltersMenu
            closeMenu={closeMenu}
            setGlobalFilters={setGlobalFilters}
            globalFilterDefinitions={globalFilterDefinitions}
            stagingGlobalFilters={stagingGlobalFilters}
            setStagingGlobalFilter={setStagingGlobalFilter}
            myObservation={myObservation}
            selectedGlobalFilterKey={selectedGlobalFilterKey}
            setSelectedGlobalFilterKey={setSelectedGlobalFilterKey}
            isEmbedMode={isEmbedMode}
          />
        );
      },
    });
  }

  if (globalFilterDefinitions.length > 0) {
    allItems.push({
      key: 'globalFilters',
      content: ({ openMenu, menuOpened }) => (
        <SecondaryFilterButton
          disabled={disabled}
          active={menuOpened}
          translatedContent={language.secondaryFilterButtonText()}
          onClick={() => {
            resetStagingGlobalFilters();
            setSelectedGlobalFilterKey(
              globalFilterDefinitions[0].columnHeaderValue,
            );
            openMenu();
          }}
        />
      ),
      menu: ({ closeMenu }) => (
        <GlobalFiltersMenu
          closeMenu={closeMenu}
          setGlobalFilters={setGlobalFilters}
          globalFilterDefinitions={globalFilterDefinitions}
          stagingGlobalFilters={stagingGlobalFilters}
          setStagingGlobalFilter={setStagingGlobalFilter}
          myObservation={myObservation}
          selectedGlobalFilterKey={selectedGlobalFilterKey}
          setSelectedGlobalFilterKey={setSelectedGlobalFilterKey}
          isEmbedMode={isEmbedMode}
        />
      ),
    });
  }

  return allItems;
};
